import { lookupsJson, rejectCodes } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { isEqualIgnoringEmpty, priorBILimitToDollars } from '@ourbranch/offer-utils';
import { policyTypes } from '@ourbranch/policy-types';
import { CAR_DIFF_PATHS_TO_OMIT, containsOmittedPath } from 'core/helpers/diff-paths';
import { currencyFormatter, capitalize } from 'core/helpers/formatters';

const subPathsMap = {
  'quote.homeClaimsForUW': 'Home Claims',
  'quote.homeClaimDates': 'Home Claims',
  'quote.homeViolations': 'Home Claims',
  'quote.home': 'Property details',
  'quote.condo.': 'Property details',
  'quote.drivers.0': 'driver',
  'quote.drivers.1': 'driver',
  'quote.drivers.2': 'driver',
  'quote.drivers.3': 'driver',
  'quote.drivers.4': 'driver',
  'quote.drivers.5': 'driver',
  'quote.drivers.6': 'driver',
  'quote.drivers.7': 'driver',
  'quote.drivers.8': 'driver',
  'quote.drivers.9': 'driver',

  'quote.nonDrivers.0': 'resident',
  'quote.nonDrivers.1': 'resident',
  'quote.nonDrivers.2': 'resident',
  'quote.nonDrivers.3': 'resident',
  'quote.nonDrivers.4': 'resident',
  'quote.nonDrivers.5': 'resident',
  'quote.nonDrivers.6': 'resident',
  'quote.nonDrivers.7': 'resident',
  'quote.nonDrivers.8': 'resident',
  'quote.nonDrivers.9': 'resident',
  'quote.cars.0': 'car0',
  'quote.cars.1': 'vehicle',
  'quote.cars.2': 'vehicle',
  'quote.cars.3': 'vehicle',
  'quote.cars.4': 'vehicle',
  'quote.cars.5': 'vehicle',
  'quote.cars.6': 'vehicle',
  'quote.cars.7': 'vehicle',
  'quote.cars.8': 'vehicle',
  'quote.cars.9': 'vehicle'
};

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\./gi;

const SKIP_PATH_PREFIXES = ['options.0.', 'options.1.', 'options.2.', 'options.3.'];
const SKIP_PATH_SUFFIXES = ['.amount'];
const SKIP_PATH_CONTAINS = ['replacementCostEstimate.itemized'];
const CLAIM_ARRAY_PATTERN = /^(?:quote\.homeClaimsForUW|quote\.(?:drivers|cars)\.\d+\.claims)\.(\d+)\.(.+)$/;
const AUTO_VIOLATION_PATTERN = /\.autoViolationDates$/;
const OUTSTANDING_RECALL_PATTERN = /^quote\.cars\.\d+\.hasOutstandingRecall$/;
const BRANDED_TITLE_PATTERN = /^quote\.cars\.(\d+)\.(?:brandedTitle|brandedTitleDescription|allowBrandedTitle)$/;
// Property claims keep their quote.home* names whatever the property is: the A-PLUS
// property pull fills the same fields on a condo offer (retrieve-data-main.ts). On a
// condo offer they belong with the condo, not under a Home card (#35689).
const PROPERTY_CLAIM_PATTERN = /^quote\.(?:homeClaimsForUW|homeClaimDates|homeViolations)/;
const PERSON_PATH_PATTERN = /^(quote\.(?:drivers|nonDrivers))\.(\d+)/;
const CAR_PATH_PATTERN = /^quote\.cars\.(\d+)/;

// Paths whose row is assembled from several sibling keys rather than rendered one
// key per row, so a per-key no-op filter would strip a piece of a real row.
const isAggregatedPath = (path) => CLAIM_ARRAY_PATTERN.test(path) || AUTO_VIOLATION_PATTERN.test(path);

const isUnwantedKey = (path) =>
  SKIP_PATH_PREFIXES.some((p) => path.startsWith(p)) ||
  SKIP_PATH_SUFFIXES.some((s) => path.endsWith(s)) ||
  SKIP_PATH_CONTAINS.some((s) => path.includes(s));

const stripUuid = (path) => path.replace(UUID_PATTERN, '');

const labelForPath = (path) => {
  const last = path.split('.').pop();
  return last.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
};

const sourceMap = {
  mvr: 'MVR',
  claims: 'Claims history'
};

export const typeToName = {
  home: 'Home',
  auto: 'Auto',
  renters: 'Renters',
  condo: 'Condo',
  monoline: 'Monoline auto',
  'monoline-home': 'Monoline home',
  'monoline-condo': 'Monoline condo'
};

const SECTION_DEFS = [
  {
    key: 'coverages',
    title: 'Coverages & billing',
    // Filled only by CONDO_LIMITS. Matching every quote.condoCoverage path would show
    // raw fields nobody decided on.
    match: () => false
  },
  {
    key: 'home',
    title: 'Home',
    match: (path) =>
      path.startsWith('quote.home.') ||
      path.startsWith('quote.homeClaimsForUW') ||
      path.startsWith('quote.homeClaimDates') ||
      path.startsWith('quote.homeViolations')
  },
  {
    key: 'condo',
    title: 'Condo',
    // quote.condoCoverage does not match: its rows come from CONDO_LIMITS.
    match: (path) => path.startsWith('quote.condo.')
  },
  {
    key: 'people',
    title: 'People',
    match: (path) => path.startsWith('quote.drivers.') || path.startsWith('quote.nonDrivers.')
  },
  {
    key: 'cars',
    title: 'Cars',
    match: (path) => path.startsWith('quote.cars.')
  }
];

// sensitive pii lives in SENSITIVE_DIFF_PATHS_TO_OMIT (core/helpers/diff-paths) and is always omitted.
const pathsToOmit = [
  ...CAR_DIFF_PATHS_TO_OMIT,
  'cherre',
  'postBindMVR',
  'gotDrivingRecord',
  'addDate',
  'driverSource',
  'returned',
  'homeLocation',
  'condoLocation',
  // The credit pull fills mortgageDetails from the bureau's open mortgages, full loan
  // number included (retrieve-data-tu.js). It is an account number, so it stays off
  // screen (#35689). Substring match covers loanNumberLast4 too.
  'loanNumber',
  'address',
  'city',
  'state',
  'autoViolationDatesAsString',
  'county',
  'initial',
  'mvrOrderNumber',
  // Substring match, so this covers insuranceScoreOverrideValue too.
  'insuranceScoreOverride',
  // Substring match covers fpcOverrideValue/Reason/By too (#35212).
  'fpcOverride',
  // Car fields that are noise in a report diff but are shown by the policy-changes
  // preview, so they cannot live in the shared CAR_DIFF_PATHS_TO_OMIT.
  'make',
  'model',
  'bodyType',
  'engine',
  'lastCarFaxDataPullDate',
  'vehicleHistoryRetrievedDate',
  'vehicleHistoryStatus',
  'verifiedAnnualMilesDriven'
];

// pathsToOmit matches substrings on purpose, but 'city' also catches garageCarCapacity,
// an editable home characteristic, so it is let through by name.
const OMITTED_PATH_EXCEPTIONS = /\.garageCarCapacity$/;

// Cape Analytics fields are shown for what they did, not because the value moved (#35031).
// Matched on the last path segment, so the rules hold for home and condo alike, and
// 'acres' leaves a farm's numberOfAcres alone.
const CAPE_FIELDS_NEVER_SHOWN = new Set([
  // No underwriting rule reads yard debris, so it can't cause a reject.
  'yardDebrisSqFt',
  'yardDebrisConfidence',
  // Inputs to roof-year selection. When they move the roof year, the roof year row shows.
  'roofReplacementYearMax',
  'roofYearConfidence',
  'roofReplacementDetected',
  'acres'
]);
// Only explains something when the CAPE_ROOF_CONDITION reject fired. The reject is
// skipped when the imagery predates the claimed roof year (#32310), so a new value
// alone does not mean it fired.
const CAPE_ROOF_CONDITION_FIELDS = new Set(['roofCondition', 'roofConditionConfidence']);
// Panels feed the replacement cost, so a new detection is worth a row. A count that
// only moved, or panels that went away, is not.
const CAPE_SOLAR_PANEL_FIELDS = new Set(['hasRoofSolarPanels', 'roofSolarPanels']);
// true or a positive count. Some report pulls write these as text, and 'false' and '0'
// are not empty values to isEmptyDiffValue, so they are read by value here.
const hasSolarPanels = (value) => Number(value) > 0 || String(value).trim().toLowerCase() === 'true';

const isIrrelevantCapeChange = ({ path, oldValue, newValue, roofConditionRejected }) => {
  const field = path.split('.').pop();
  if (CAPE_FIELDS_NEVER_SHOWN.has(field)) {
    return true;
  }
  if (CAPE_ROOF_CONDITION_FIELDS.has(field)) {
    return !roofConditionRejected;
  }
  if (CAPE_SOLAR_PANEL_FIELDS.has(field)) {
    return hasSolarPanels(oldValue) || !hasSolarPanels(newValue);
  }
  return false;
};

// Prior BI limits live on quote.global as two full-dollar fields (100000 / 300000), but
// a reader knows them as the split-limit shorthand every other limit on the offer
// uses: "100/300". The modal folds the pair into one "Prior BI Limit" row under the
// primary applicant — the prior policy was theirs.
const PRIOR_INDIVIDUAL_BI_PATH = 'quote.global.priorIndividualBILimit';
const PRIOR_OCCURRENCE_BI_PATH = 'quote.global.priorOccurrenceBILimit';

const RCE_TOTAL_PATH = 'quote.home.replacementCostEstimate.total';
const RC1_ITEMS_CHANGED_PATH = 'quote.overrides.rc1.itemsChanged.';

// priorBILimitToDollars is the rater's own unit rule (anything under 1,000 is already
// in thousands). A zero or unparseable amount means "no prior coverage recorded" rather
// than a $0 limit — the lookup starts at $25,000 — so it reads as a missing half, which
// keeps this row agreeing with the empty-value filter above it.
const toThousands = (value) => {
  const dollars = priorBILimitToDollars(value);
  return dollars && Number.isFinite(dollars) ? dollars / 1000 : null;
};

// "100/300" from (100000, 300000); null when neither side is set. A missing half
// renders as a dash so the pair still reads as a pair.
export const formatPriorBILimit = (individual, occurrence) => {
  const parts = [toThousands(individual), toThousands(occurrence)];
  if (parts.every((part) => part === null)) {
    return null;
  }
  return parts.map((part) => (part === null ? '—' : String(part))).join('/');
};

// One row for both prior BI limit fields. A half that did not move is filled in from
// the current offer so each side of the arrow is a complete "individual/occurrence".
// Returns null when neither field is among the changed keys, or when the two sides
// format the same (for example a value re-saved in thousands instead of dollars).
const buildPriorBILimitRow = ({ changedKeys, added, removed, offer }) => {
  const changed = changedKeys.filter((path) => path === PRIOR_INDIVIDUAL_BI_PATH || path === PRIOR_OCCURRENCE_BI_PATH);
  if (changed.length === 0) {
    return null;
  }
  const current = {
    [PRIOR_INDIVIDUAL_BI_PATH]: offer?.quote?.global?.priorIndividualBILimit,
    [PRIOR_OCCURRENCE_BI_PATH]: offer?.quote?.global?.priorOccurrenceBILimit
  };
  const sideValue = (side, path) => (path in side ? side[path] : current[path]);
  const oldValue = formatPriorBILimit(
    sideValue(removed, PRIOR_INDIVIDUAL_BI_PATH),
    sideValue(removed, PRIOR_OCCURRENCE_BI_PATH)
  );
  const newValue = formatPriorBILimit(
    sideValue(added, PRIOR_INDIVIDUAL_BI_PATH),
    sideValue(added, PRIOR_OCCURRENCE_BI_PATH)
  );
  // No new value means the pair was cleared. Every other row is dropped in that case
  // (see the `!row.newValue` guard below), and the modal has no "removed" presentation —
  // it would render the old value alone, reading as if it were current.
  if (!newValue || oldValue === newValue) {
    return null;
  }
  return { path: 'quote.global.priorBILimit', label: 'Prior BI Limit', oldValue, newValue, subtitle: null };
};

// A key only earns a row when its value moved in a way a reader would call a change.
// isEqualIgnoringEmpty is shared with the backend offer diff and covers empty-to-empty
// moves such as '' to null. On top of that the modal compares scalars as trimmed,
// lower-cased text, which covers the shapes a report pull produces: casing-only edits,
// padding, a number re-serialised as a string ('4' and 4), and a boolean written as its
// own name (true and 'true').
//
// Text rather than `==`: loose equality reads both sides as numbers, so it called
// '02134' and 2134 equal and hid a zip that had lost its leading zero.
const isNoopChange = (oldValue, newValue) =>
  isEqualIgnoringEmpty(oldValue, newValue) ||
  (typeof oldValue !== 'object' &&
    typeof newValue !== 'object' &&
    String(oldValue).trim().toLowerCase() === String(newValue).trim().toLowerCase());

// Condo limits, labelled as the condo coverage form labels them (#35689). Unlike home,
// where B, C and D are shares of A, condo Coverage C is its own dollar figure: the
// valuation sets it from the replacement cost, so it can move on Run reports. There is
// no deductible row: the condo minimum is a flat amount
// (condo-underwriting-rules.ts #checkDeductibles), not a share of Coverage A.
const CONDO_LIMITS = [
  { path: 'quote.condoCoverage.coverageA', label: 'Building property protection (Coverage A)' },
  { path: 'quote.condoCoverage.coverageC', label: 'Personal property limit (Coverage C)' }
];

const formatDollars = (value) => currencyFormatter(Number(value), { removeDecimal: true });

// A limit row in dollars, or null when the limit did not move or has no new amount.
const buildLimitRow = ({ path, label, added, removed }) => {
  if (!(Number(added[path]) > 0) || isNoopChange(removed[path], added[path])) {
    return null;
  }
  return {
    path,
    label,
    oldValue: Number(removed[path]) > 0 ? formatDollars(removed[path]) : null,
    newValue: formatDollars(added[path]),
    subtitle: null
  };
};

export const parseDiff = (offerDifference) => {
  if (!offerDifference) {
    return { added: {}, removed: {} };
  }
  try {
    return {
      added: JSON.parse(offerDifference.addedChanges ?? '{}'),
      removed: JSON.parse(offerDifference.removedChanges ?? '{}')
    };
  } catch {
    return { added: {}, removed: {} };
  }
};

const getSubSectionLabel = ({ matchedKey, mappedLabel, offer }) => {
  const personMatch = matchedKey.match(PERSON_PATH_PATTERN);
  if (personMatch) {
    const [, collectionPath, index] = personMatch;
    const collection = collectionPath === 'quote.drivers' ? offer?.quote?.drivers : offer?.quote?.nonDrivers;
    const person = collection?.[Number(index)];
    if (!person) {
      return { subSectionToAddKey: mappedLabel };
    }

    const fullName = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
    return { subSectionToAddKey: fullName || mappedLabel, primary: person.isPrimary, coApp: person.isCoApplicant };
  }

  const carMatch = matchedKey.match(CAR_PATH_PATTERN);
  if (carMatch) {
    const car = offer?.quote?.cars?.[Number(carMatch[1])];
    if (!car) {
      return { subSectionToAddKey: mappedLabel };
    }

    const carLabel = [car.make, car.model, car.year].filter(Boolean).join(' ').trim();
    return { subSectionToAddKey: carLabel || mappedLabel };
  }

  return { subSectionToAddKey: mappedLabel };
};

const HOME_PRODUCT = { label: 'Home', totalKey: 'homeTotal', policyType: 'home' };
const CONDO_PRODUCT = { label: 'Condo', totalKey: 'condoTotal', policyType: 'condo' };
const RENTERS_PRODUCT = { label: 'Renters', totalKey: 'rentersTotal', policyType: 'renters' };
const AUTO_PRODUCT = { label: 'Auto', totalKey: 'autoTotal', policyType: 'auto' };
const FARM_PRODUCT = { label: 'Farm', totalKey: 'farmTotal', policyType: 'farm' };

// Pick the product rows to render for the selected option's line-up. Farm offers
// price a Farm line from `farmTotal`; on a farm option the home/auto totals are
// null placeholders, so a home+auto row set would render them as bogus
// "$0.00" rows and never show the actual farm premium (#33659). isFarmBasedPolicyType
// covers only the monoline codes F/Z/Y, so the FA (Farm & Auto) bundle — a real,
// selectable product — is handled explicitly and adds the Auto line.
const productsForSelectedOption = (selectedOption) => {
  if (isFarmBasedPolicyType(selectedOption)) {
    return [FARM_PRODUCT];
  }
  if (selectedOption === policyTypes.FABundle) {
    return [FARM_PRODUCT, AUTO_PRODUCT];
  }
  const products = [];
  if (selectedOption?.includes(policyTypes.Home)) {
    products.push(HOME_PRODUCT);
  } else if (selectedOption?.includes(policyTypes.Condo)) {
    products.push(CONDO_PRODUCT);
  } else if (selectedOption?.includes(policyTypes.Renters)) {
    // On the AR bundle `rentersTotal` is only real in standalone-renters states (a separate
    // renters policy); elsewhere renters is an endorsement folded into `autoTotal` and
    // `rentersTotal` stays a null placeholder, so the null guard below drops this row.
    products.push(RENTERS_PRODUCT);
  }
  if (selectedOption?.includes(policyTypes.Auto)) {
    products.push(AUTO_PRODUCT);
  }
  return products;
};

export const buildPricingRows = ({ offer, removed, eligibilityChanges }) => {
  const selectedOption = offer?.quote?.selectedOption;
  const selectedIdx = offer?.options?.findIndex((opt) => opt?.type === selectedOption) ?? -1;
  if (selectedIdx < 0) {
    return [];
  }

  const selectedOpt = offer.options[selectedIdx];

  const newlyIneligible = (policy) =>
    eligibilityChanges.some((c) => c.policyType === policy && c.wasEligible === true && c.isEligible === false);

  const rentersEndorsement =
    (offer?.quote?.includeRenters || selectedOption?.includes(policyTypes.Renters)) &&
    selectedOpt?.rentersTotal == null;

  const policyWithEndorsementLabel = (label, totalKey) => {
    if (totalKey === 'autoTotal' && rentersEndorsement) {
      return 'Auto & Renters';
    }
    if (totalKey === 'homeTotal' && offer?.quote?.includeUmbrella) {
      return 'Home & Umbrella';
    }
    return label;
  };

  return productsForSelectedOption(selectedOption).reduce((rows, { label, totalKey, policyType }) => {
    const oldKey = `options.${selectedIdx}.${totalKey}`;
    // Options carry null placeholders for lines they don't price, so null hides the row too.
    const show = oldKey in removed || selectedOpt?.[totalKey] != null;
    const previousTotal = removed[oldKey];
    const currentTotal = selectedOpt?.[totalKey];
    const changed = oldKey in removed && !isNoopChange(previousTotal, currentTotal);
    if (show) {
      const ineligible = newlyIneligible(policyType);
      rows.push({
        label: policyWithEndorsementLabel(label, totalKey),
        changed,
        // Totals arrive as strings from some report pulls; currencyFormatter delegates to
        // String#toLocaleString for those, which ignores the fraction options and drops
        // the cents ('$400' beside '$450.00').
        oldValue: changed ? currencyFormatter(Number(previousTotal)) : null,
        newValue: ineligible ? 'Ineligible' : currencyFormatter(Number(currentTotal)),
        newlyIneligible: ineligible
      });
    }
    return rows;
  }, []);
};

export const buildSectionChanges = ({ added, removed, offer, eligibilityChanges = [] }) => {
  const roofConditionRejected = eligibilityChanges.some((change) =>
    change.rejectionCodesAdded?.includes(rejectCodes.CAPE_ROOF_CONDITION)
  );
  const allKeys = Array.from(new Set([...Object.keys(added), ...Object.keys(removed)]))
    .filter(
      (p) =>
        !isUnwantedKey(p) &&
        (OMITTED_PATH_EXCEPTIONS.test(p) || !containsOmittedPath(p, pathsToOmit)) &&
        !isIrrelevantCapeChange({ path: p, oldValue: removed[p], newValue: added[p], roofConditionRejected }) &&
        // Claim and violation rows are built by combining sibling keys into one label
        // (lossTypeCode + claimGroup + lossDate). Dropping one of them per key left the
        // row reading "claim (type undefined)", so these are judged as whole rows below.
        (isAggregatedPath(p) || !isNoopChange(removed[p], added[p]))
    )
    .sort();

  const sections = SECTION_DEFS.map((s) => ({ ...s, sections: {} }));
  const brandedTitleRowCars = new Set();
  // The pre-report selection: when the reports make the condo ineligible, the offerings
  // updater drops C from the selection (CA becomes A), and those claims are still condo claims.
  const selectedOptionBeforeReports =
    'quote.selectedOption' in removed ? removed['quote.selectedOption'] : offer?.quote?.selectedOption;
  const isCondoOffer = selectedOptionBeforeReports?.includes(policyTypes.Condo);
  for (const path of allKeys) {
    const condoClaim = isCondoOffer && PROPERTY_CLAIM_PATTERN.test(path);
    const section = condoClaim ? sections.find((s) => s.key === 'condo') : sections.find((s) => s.match(path));
    if (!section) {
      continue;
    }

    const matchedKey = Object.keys(subPathsMap).find((k) => path.includes(k));
    const mappedLabel = condoClaim ? 'Condo Claims' : subPathsMap[matchedKey];

    if (!mappedLabel) {
      continue;
    }

    const { subSectionToAddKey, primary, coApp } = getSubSectionLabel({ matchedKey, mappedLabel, offer });
    if (!subSectionToAddKey) {
      continue;
    }
    if (!section.sections[subSectionToAddKey]) {
      section.sections[subSectionToAddKey] = { primary, coApp, rows: [], claims: {} };
    }
    const subSection = section.sections[subSectionToAddKey];

    const claimMatch = path.match(CLAIM_ARRAY_PATTERN);
    if (claimMatch) {
      const [, claimIndex, fieldPath] = claimMatch;
      const newValue = added[path];
      if (newValue === null || newValue === undefined) {
        continue;
      }
      if (!subSection.claims[claimIndex]) {
        subSection.claims[claimIndex] = {};
      }
      subSection.claims[claimIndex][fieldPath] = String(newValue);

      continue;
    }

    if (AUTO_VIOLATION_PATTERN.test(path)) {
      const rawValue = added[path];
      if (rawValue === null || rawValue === undefined) {
        continue;
      }
      let violationsByDate;
      try {
        violationsByDate = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
      } catch {
        continue;
      }
      Object.entries(violationsByDate).forEach(([violationDate, violations]) => {
        (violations || []).forEach((violation, index) => {
          const source = violation.isCourtRecord ? 'Court records' : violation.source;
          subSection.rows.push({
            path: `${path}-${violationDate}-${index}`,
            label: `${lookupsJson.autoViolationClassDescriptions[violation.class]} (type ${violation.class})`,
            oldValue: null,
            newValue: null,
            subtitle: `${violationDate} • Source: ${sourceMap[source] ? sourceMap[source] : capitalize(source)}`
          });
        });
      });

      continue;
    }

    if (OUTSTANDING_RECALL_PATTERN.test(path) && (added[path] === true || added[path] === 'true')) {
      subSection.rows.push({
        path,
        label: 'Vehicle’s make/model has an outstanding recall'
      });

      continue;
    }

    const brandedTitleMatch = path.match(BRANDED_TITLE_PATTERN);
    if (brandedTitleMatch) {
      const [, carIndex] = brandedTitleMatch;
      const carPath = `quote.cars.${carIndex}`;
      const car = offer?.quote?.cars?.[Number(carIndex)];
      const newlyBranded = added[`${carPath}.brandedTitle`] === true;
      // while allowBrandedTitle is false the rater re-evaluates it on every pull, so it can flip on a branded car
      const newlyAllowed = added[`${carPath}.allowBrandedTitle`] === true;

      if (newlyBranded || (newlyAllowed && car?.brandedTitle)) {
        if (!brandedTitleRowCars.has(carIndex)) {
          brandedTitleRowCars.add(carIndex);
          const rowPath = `${carPath}.brandedTitle`;
          const brandedTitleDescription = added[`${carPath}.brandedTitleDescription`];
          const brandedTitleLabel = brandedTitleDescription
            ? `Branded title (${brandedTitleDescription})`
            : 'Branded title';

          // Allowed branded titles (Rebuilt/Lemon buyback) now keep full coverage (#35176), so the diff
          // just notes the title change — no comp/coll or liability-only qualifier.
          subSection.rows.push({ path: rowPath, label: brandedTitleLabel });
        }

        continue;
      }
    }

    if (path === RCE_TOTAL_PATH) {
      // rc1.itemsChanged is written once, by the save that runs reports, and kept; only that save's diff adds it.
      const switchedToOurValue = Object.entries(added).some(
        ([changedPath, value]) => changedPath.startsWith(RC1_ITEMS_CHANGED_PATH) && value === 'coverageA'
      );
      subSection.rows.push({
        path,
        label: 'Replacement cost estimate',
        oldValue:
          removed[path] !== undefined ? currencyFormatter(Number(removed[path]), { removeDecimal: true }) : null,
        newValue: added[path] !== undefined ? currencyFormatter(Number(added[path]), { removeDecimal: true }) : null,
        subtitle: switchedToOurValue ? 'Now using our calculated value' : null
      });

      continue;
    }

    const row = {
      path,
      label: labelForPath(stripUuid(path)),
      oldValue: removed[path] !== undefined ? String(removed[path]) : null,
      newValue: added[path] !== undefined ? String(added[path]) : null,
      subtitle: null
    };

    if (!row.newValue) {
      continue;
    }

    subSection.rows.push(row);
  }

  for (const section of sections) {
    for (const subSection of Object.values(section.sections)) {
      Object.keys(subSection.claims)
        .sort((a, b) => Number(a) - Number(b))
        .forEach((claimIndex) => {
          const claim = subSection.claims[claimIndex];

          subSection.rows.push({
            path: `claim-${claimIndex}`,
            label: `${capitalize(claim.lossTypeCode)} claim (type ${claim.claimGroup})`,
            oldValue: null,
            newValue: null,
            subtitle: `${claim.lossDate} • Source: Claim History`
          });
        });
      delete subSection.claims;
    }
  }

  sections.find((s) => s.key === 'coverages').sections['Condo coverage'] = {
    rows: CONDO_LIMITS.map((limit) => buildLimitRow({ ...limit, added, removed })).filter(Boolean)
  };

  // The prior BI limit row goes under the primary applicant's name, labelled the same
  // way as their own rows. Falls back to the first driver when no one is flagged primary.
  const priorBILimitRow = buildPriorBILimitRow({ changedKeys: allKeys, added, removed, offer });
  if (priorBILimitRow) {
    const drivers = offer?.quote?.drivers ?? [];
    const primaryIndex = Math.max(
      drivers.findIndex((driver) => driver?.isPrimary),
      0
    );
    const { subSectionToAddKey, primary, coApp } = getSubSectionLabel({
      matchedKey: `quote.drivers.${primaryIndex}`,
      mappedLabel: subPathsMap['quote.drivers.0'],
      offer
    });
    const peopleSection = sections.find((s) => s.key === 'people');
    if (!peopleSection.sections[subSectionToAddKey]) {
      peopleSection.sections[subSectionToAddKey] = { primary, coApp, rows: [] };
    }
    peopleSection.sections[subSectionToAddKey].rows.push(priorBILimitRow);
  }

  for (const section of sections) {
    for (const key of Object.keys(section.sections)) {
      if (section.sections[key].rows.length === 0) {
        delete section.sections[key];
      }
    }
  }

  return sections.filter((s) => Object.keys(s.sections).length > 0);
};

export const REPORT_RESULTS_SHOWN_EVENT = 'Staff - Report Results Shown';

const VIOLATION_ROW_SUFFIX_PATTERN = /(\.autoViolationDates)-.*$/;
const CLAIM_ROW_PATTERN = /^claim-\d+$/;
const DATE_SEGMENT_PATTERN = /\.\d{4}-\d{2}-\d{2}(?=\.|$)/g;
const ARRAY_INDEX_PATTERN = /\.\d+(?=\.|$)/g;

// Collapse index-specific and synthetic row paths so the weekly usage report counts a field once.
export const normalizeFieldPath = (path) =>
  stripUuid(path)
    .replace(VIOLATION_ROW_SUFFIX_PATTERN, '$1')
    .replace(CLAIM_ROW_PATTERN, 'claims')
    .replace(DATE_SEGMENT_PATTERN, '[]')
    .replace(ARRAY_INDEX_PATTERN, '[]');

const fieldKey = ({ section, path, label }) => `${section}|${path}|${label}`;

const REPORT_PRICED_LINES = ['home', 'auto', 'condo'];

// Version 1 (no property on the event) read the pre-report offer, because the modal opened before the
// store swapped in the new one, so every *TotalChange was 0 and offerId was the old offer's (#35812).
// Charts of the premium fields filter on this to leave those events out.
export const REPORT_PREMIUMS_VERSION = 2;

// Numeric premium of the selected option before and after the report pull (the diff's `removed`
// side holds the pre-report value of anything that changed), so the price movement caused by
// reports is chartable per prospect instead of only as formatted pricing rows.
export const buildReportPremiumChange = ({ offer, removed = {} }) => {
  const selectedOption = offer?.quote?.selectedOption;
  const selectedIdx = offer?.options?.findIndex((opt) => opt?.type === selectedOption) ?? -1;
  if (selectedIdx < 0) {
    return {};
  }
  const selectedOpt = offer.options[selectedIdx];
  return REPORT_PRICED_LINES.reduce((acc, line) => {
    const totalKey = `${line}Total`;
    const oldKey = `options.${selectedIdx}.${totalKey}`;
    const changed = oldKey in removed;
    if (!changed && selectedOpt?.[totalKey] == null) {
      return acc;
    }
    const toNumber = (value) => (value == null || Number.isNaN(Number(value)) ? undefined : Number(value));
    const after = toNumber(selectedOpt?.[totalKey]);
    const before = changed ? toNumber(removed[oldKey]) : after;
    acc[`${line}TotalBeforeReports`] = before;
    acc[`${line}TotalAfterReports`] = after;
    if (before != null && after != null) {
      acc[`${line}TotalChange`] = Number((after - before).toFixed(2));
    }
    return acc;
  }, {});
};

// Segment payload per modal open (#33917): field names only, never values (premiums aside).
export const buildReportResultsShownEvent = ({
  offer,
  state,
  isAgency,
  sections,
  pricingRows,
  rejectionCount,
  removed
}) => {
  const fieldsByKey = new Map();
  let rowCount = 0;
  for (const section of sections) {
    for (const { rows } of Object.values(section.sections)) {
      for (const row of rows) {
        rowCount += 1;
        const field = { path: normalizeFieldPath(row.path), label: row.label, section: section.key };
        fieldsByKey.set(fieldKey(field), field);
      }
    }
  }
  const fields = [...fieldsByKey.values()].sort((a, b) => fieldKey(a).localeCompare(fieldKey(b)));

  return {
    offerId: offer?.id,
    state,
    isAgency: Boolean(isAgency),
    isRc1Offer: Boolean(offer?.quote?.global?.fromRC1),
    selectedOption: offer?.quote?.selectedOption ?? null,
    fieldCount: fields.length,
    rowCount,
    pricingRowCount: pricingRows.length,
    rejectionCount,
    hasAnyContent: sections.length > 0 || rejectionCount > 0,
    fields: JSON.stringify(fields),
    webUserId: offer?.webUserId,
    reportPremiumsVersion: REPORT_PREMIUMS_VERSION,
    ...buildReportPremiumChange({ offer, removed })
  };
};
